import React from 'react';

// Matches errors thrown when a dynamically imported chunk can't be fetched,
// typically because the file was removed by a newer deployment while this
// tab still holds references to the previous build's hashed filenames.
const CHUNK_LOAD_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|Importing a module script failed|Loading (chunk|CSS chunk) \d+ failed/i;

const CHUNK_RELOAD_SESSION_KEY = 'sfs-chunk-reload';

function isChunkLoadError(error) {
  return Boolean(error && CHUNK_LOAD_ERROR_PATTERN.test(error.message || ''));
}

class ErrorBoundary extends React.Component {
  // Constructor for initializing Variables etc in a state
  // Just similar to initial line of useState if you are familiar
  // with Functional Components
  constructor(props) {
    super(props);
    this.state = { error: null, errorInfo: null };
  }

  // This method is called if any error is encountered
  componentDidCatch(error, errorInfo) {
    if (isChunkLoadError(error)) {
      // Reload once per session to fetch the latest build instead of
      // getting stuck on a broken screen. Avoid infinite reload loops if
      // the error persists across reloads.
      if (!sessionStorage.getItem(CHUNK_RELOAD_SESSION_KEY)) {
        sessionStorage.setItem(CHUNK_RELOAD_SESSION_KEY, '1');
        window.location.reload();
        return;
      }
    }

    // Catch errors in any components below and
    // re-render with error message
    this.setState({
      error: error,
      errorInfo: errorInfo,
    });

    // You can also log error messages to an error
    // reporting service here
  }

  // This will render this component wherever called
  render() {
    if (this.state.errorInfo) {
      // Error path
      return (
        <div>
          <h2>An Error Has Occured</h2>
          <details>
            {this.state.error && this.state.error.toString()}
            <br />
            {this.state.errorInfo.componentStack}
          </details>
        </div>
      );
    }
    // Normally, just render children, i.e. in
    // case no error is Found
    return this.props.children;
  }
}

export default ErrorBoundary;
